import React, { useEffect, useState } from "react";
import OPPCss from "../../Dashboard/Opportunities/Opportunities.module.scss";
import Sidebar from "../Sidebar/Sidebar";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader/Loader";
import LocationDropdown from "../../Dashboard/VenueListComp/LocationDropdown";
import "../../Dashboard/VenueListComp/VenueList.scss";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";

function MembersList() {
  const navigate = useNavigate();

  const [hideSvg, setHideSvg] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("All"); // Default to All
  const [startDate, setStartDate] = useState(null); // Use null for DatePicker
  const [endDate, setEndDate] = useState(null); // Use null for DatePicker
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStartDate, setModalStartDate] = useState(null); // Modal start date
  const [modalEndDate, setModalEndDate] = useState(null); // Modal end date

  const handleSvg = () => {
    setHideSvg(!hideSvg);
  };

  const formatDate = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchMembers = async () => {
    setLoading(true);
    const token = localStorage.getItem("access_token");
    let url = `https://ballersapi.ballerslife.co/api/v1/admin/membership/admin/all_members`;

    // Build query parameters only if filters are applied
    const queryParams = [];
    if (paymentStatus !== "All") {
      queryParams.push(`payment_status=${paymentStatus.toLowerCase()}`);
    }
    if (startDate) {
      const formattedStartDate = formatDate(startDate);
      queryParams.push(`start_date=${formattedStartDate}`);
    }
    if (endDate) {
      const formattedEndDate = formatDate(endDate);
      queryParams.push(`end_date=${formattedEndDate}`);
    }

    // Append query parameters if any exist
    if (queryParams.length > 0) {
      url += `?${queryParams.join("&")}`;
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      setMembers(data.members || []);
      setNoData(data.members.length === 0);
      setLoading(false);
    } catch (err) {
      setNoData(true);
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      toast.error("Authentication token not found. Please log in again.");
      return;
    }

    // Validate modal dates
    if (!modalStartDate && modalEndDate) {
      toast.error("Please select a start date.");
      return;
    }
    if (modalStartDate && modalEndDate && modalStartDate > modalEndDate) {
      toast.error("Start date cannot be after end date.");
      return;
    }

    // Construct URL with query parameters
    const queryParams = ["download=excel"];
    if (modalStartDate) {
      queryParams.push(`start_date=${formatDate(modalStartDate)}`);
    }
    if (modalEndDate) {
      queryParams.push(`end_date=${formatDate(modalEndDate)}`);
    }

    const url = `https://ballersapi.ballerslife.co/api/v1/admin/admin/members/download_data?${queryParams.join(
      "&"
    )}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(
            errorData.detail || `HTTP error! Status: ${response.status}`
          );
        } else {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
      }

      const contentType = response.headers.get("content-type");
      if (
        !contentType ||
        !contentType.includes(
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        )
      ) {
        throw new Error("Unexpected response format. Expected an Excel file.");
      }

      // Get file name from Content-Disposition header
      let fileName = `members_${new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "")}.xlsx`;
      const disposition = response.headers.get("content-disposition");
      if (disposition && disposition.includes("attachment")) {
        const fileNameMatch = disposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("File downloaded successfully!");
      setIsModalOpen(false);
      setModalStartDate(null);
      setModalEndDate(null);
    } catch (err) {
      console.error("Error downloading file:", err);
      toast.error(
        err.message || "Failed to download the file. Please try again."
      );
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [paymentStatus, startDate, endDate]); // Re-fetch when paymentStatus, startDate, or endDate changes

  const membersDetails = (member) => {
    navigate(`/admin/members/details/${member.user_id}`, {
      state: { member: member.user_id },
    });
  };

  useEffect(() => {
    document.title = "All Members | Ballers";
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="dashMain">
      <div className="sideBar">
        <Sidebar />
      </div>
      {hideSvg ? (
        <div className="sideBarMobile">
          <Sidebar />
        </div>
      ) : (
        <div></div>
      )}
      <div className="content">
        <div className="filtersAllBooking">
          <div className="dropdownAllBooking">
            <label htmlFor="payment-status-select">Status:</label>
            <LocationDropdown
              location={paymentStatus}
              setLocation={setPaymentStatus}
              locationOptions={["All", "Paid", "Unpaid"]}
            />
          </div>
          <div className="dropdownAllBookingMemberList">
            <label htmlFor="start-date">Start Brand Date:</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select start date"
              className="datePickerMemberList"
            />
          </div>
          <div className="dropdownAllBookingMemberList">
            <label htmlFor="end-date">End Brand Date:</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select end date"
              className="datePickerMemberList"
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FFBF00"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="downloadIconExcelDownload"
              onClick={() => setIsModalOpen(true)}
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </div>
        </div>
        {isModalOpen && (
          <div className="modalExcelDownload">
            <div className="modalContentExcelDownload">
              <span
                className="closeModalExcelDownload"
                onClick={() => setIsModalOpen(false)}
              >
                ×
              </span>
              <h2 className="modalTitleExcelDownload">Download Members Data</h2>
              <div className="modalFieldExcelDownload">
                <label htmlFor="modal-start-date">Start Date:</label>
                <DatePicker
                  selected={modalStartDate}
                  onChange={(date) => setModalStartDate(date)}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select start date"
                  className="datePickerModalExcelDownload"
                />
              </div>
              <div className="modalFieldExcelDownload">
                <label htmlFor="modal-end-date">End Date:</label>
                <DatePicker
                  selected={modalEndDate}
                  onChange={(date) => setModalEndDate(date)}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select end date"
                  className="datePickerModalExcelDownload"
                />
              </div>
              <button
                className="downloadButtonExcelDownload"
                onClick={handleDownload}
              >
                Download
              </button>
            </div>
          </div>
        )}
        {loading ? (
          <Loader />
        ) : noData ? (
          <div className={OPPCss.fcAllBookingMain}>No members available.</div>
        ) : (
          <>
            <div className={OPPCss.fcAllBookingMain}>
              <div className={OPPCss.fcAllBookingHeaderMembers}>
                <div className={OPPCss.fcAllBookingCell}>
                  <p className="investmentHeader">S.No</p>
                </div>
                <div className={OPPCss.fcAllBookingCell}>
                  <p className="investmentHeader">NAME</p>
                </div>
                <div className={OPPCss.fcAllBookingCell}>
                  <p className="investmentHeader">PHONE NO</p>
                </div>
                <div className={OPPCss.fcAllBookingCell}>
                  <p className="investmentHeader">TRIAL START DATE</p>
                </div>
                <div className={OPPCss.fcAllBookingCell}>
                  <p className="investmentHeader">TRIAL END DATE</p>
                </div>
                <div className={OPPCss.fcAllBookingCell}>
                  <p className="investmentHeader">PAYMENT STATUS</p>
                </div>
              </div>
              {members.map((member, index) => (
                <div
                  className={OPPCss.fcAllBookingRowMembers}
                  key={member.id}
                  onClick={() => membersDetails(member)}
                >
                  <div className={OPPCss.fcAllBookingCell}>
                    <p>{index + 1}.</p>
                  </div>
                  <div className={OPPCss.fcAllBookingCell}>
                    <p>{member.member_name || "N/A"}</p>
                  </div>
                  <div className={OPPCss.fcAllBookingCell}>
                    <p>
                      {member.country_code || ""}
                      {member.phone_no || "N/A"}
                    </p>
                  </div>
                  <div className={OPPCss.fcAllBookingCell}>
                    <p>{member.trial_start_date || "N/A"}</p>
                  </div>
                  <div className={OPPCss.fcAllBookingCell}>
                    <p>{member.trial_end_date || "N/A"}</p>
                  </div>
                  <div className={OPPCss.fcAllBookingCell}>
                    <p>{member.payment_status || "N/A"}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MembersList;
